// Pure alignment helpers for the OverlayEditor multi-select.
//
// `alignToAnchor` aligns every selected placement EXCEPT the last-clicked (the
// anchor) to the anchor's edge/center — "align to last selected". `distribute*`
// take the full placements array + a Set<fieldId> of selected placements and
// return a new placements array; non-selected placements are unchanged. All
// math operates in the canvas-percent coordinate space the editor already uses.
//
// Visual bounds account for the placement's `textAnchor` (and image width when
// the field is an image):
//   - text with a width (box-model `w`, or legacy containerW):
//       left   anchor: visual-left = x,           visual-right = x + w
//       center anchor: visual-left = x - w/2,     visual-right = x + w/2
//       right  anchor: visual-left = x - w,       visual-right = x
//   - text without a width: point-positioned (visual-left = visual-right = x)
//   - image (dataType === 'Image' or imageWidth defined):
//       same anchor rules apply with imageWidth as the bounding-box width.
//
// distributeHorizontal/Vertical center alignment uses the median of the
// selected bounding box, i.e. (minLeft + maxRight) / 2 — not the mean of
// centers. This keeps it idempotent and visually balanced even when one
// element is much wider.

const round2 = (n) => +Number(n).toFixed(2)

function getWidth(p) {
  // Prefer the box-model width (p.w) when present, then legacy imageWidth for
  // image fields, then legacy containerW for point-anchored text.
  if (p.w !== undefined && p.w !== null) return p.w
  if (p.imageWidth !== undefined && p.imageWidth !== null) return p.imageWidth
  if (p.containerW !== undefined && p.containerW !== null) return p.containerW
  return 0
}

function getHeight(p) {
  // Box-model height (p.h) for text, then legacy imageHeight for image fields.
  if (p.h !== undefined && p.h !== null) return p.h
  if (p.imageHeight !== undefined && p.imageHeight !== null) return p.imageHeight
  return 0
}

function getAnchor(p) {
  // Prefer the box-model anchor (p.hAlign) when present, then legacy
  // textAnchor, defaulting to 'center' (matches the rest of the editor);
  // images without an explicit anchor are centered too.
  return p.hAlign ?? p.textAnchor ?? 'center'
}

export function getVisualLeft(p) {
  const w = getWidth(p)
  if (w === 0) return p.x
  const a = getAnchor(p)
  if (a === 'left') return p.x
  if (a === 'right') return p.x - w
  return p.x - w / 2
}

export function getVisualRight(p) {
  const w = getWidth(p)
  if (w === 0) return p.x
  const a = getAnchor(p)
  if (a === 'left') return p.x + w
  if (a === 'right') return p.x
  return p.x + w / 2
}

export function getVisualCenterX(p) {
  return (getVisualLeft(p) + getVisualRight(p)) / 2
}

// Compute the x value that places the placement's visual-left at `targetLeft`.
function xForVisualLeft(p, targetLeft) {
  const w = getWidth(p)
  if (w === 0) return targetLeft
  const a = getAnchor(p)
  if (a === 'left') return targetLeft
  if (a === 'right') return targetLeft + w
  return targetLeft + w / 2
}

// Compute the x value that places the placement's visual-right at `targetRight`.
function xForVisualRight(p, targetRight) {
  const w = getWidth(p)
  if (w === 0) return targetRight
  const a = getAnchor(p)
  if (a === 'left') return targetRight - w
  if (a === 'right') return targetRight
  return targetRight - w / 2
}

// Compute the x value that places the placement's visual-center at `targetCenter`.
function xForVisualCenter(p, targetCenter) {
  const w = getWidth(p)
  if (w === 0) return targetCenter
  const a = getAnchor(p)
  if (a === 'left') return targetCenter - w / 2
  if (a === 'right') return targetCenter + w / 2
  return targetCenter
}

function selected(placements, selection) {
  return placements.filter((p) => selection.has(p.fieldId))
}

// Align every OTHER placement in `orderedIds` to the LAST id's (the anchor's)
// edge/center — the "align to last selected" model. The anchor itself is
// never moved. `orderedIds` is the selection in click order; a plain click
// with a single id is not alignable (need at least a mover + an anchor).
export function alignToAnchor(placements, orderedIds, edge) {
  if (!orderedIds || orderedIds.length < 2) return placements
  const anchorId = orderedIds[orderedIds.length - 1]
  const moverIds = new Set(orderedIds.slice(0, -1))
  const anchor = placements.find((p) => p.fieldId === anchorId)
  if (!anchor) return placements

  let apply
  switch (edge) {
    case 'left': {
      const target = getVisualLeft(anchor)
      apply = (p) => ({ ...p, x: round2(xForVisualLeft(p, target)) })
      break
    }
    case 'right': {
      const target = getVisualRight(anchor)
      apply = (p) => ({ ...p, x: round2(xForVisualRight(p, target)) })
      break
    }
    case 'centerH': {
      const target = getVisualCenterX(anchor)
      apply = (p) => ({ ...p, x: round2(xForVisualCenter(p, target)) })
      break
    }
    // Vertical: box-model y is the box top (no positioning anchor). Align the
    // mover's corresponding edge/center to the anchor's, accounting for height.
    case 'top': {
      const target = anchor.y
      apply = (p) => ({ ...p, y: round2(target) })
      break
    }
    case 'bottom': {
      const target = anchor.y + getHeight(anchor)
      apply = (p) => ({ ...p, y: round2(target - getHeight(p)) })
      break
    }
    case 'centerV': {
      const target = anchor.y + getHeight(anchor) / 2
      apply = (p) => ({ ...p, y: round2(target - getHeight(p) / 2) })
      break
    }
    default:
      return placements
  }

  return placements.map((p) => (moverIds.has(p.fieldId) ? apply(p) : p))
}

// Distribute selected placements so the gaps between their visual centers are
// equal along the horizontal axis. The first and last (by visual-center-x) stay
// fixed; the middle placements are repositioned. No-op for fewer than 3
// selections (you need at least one "middle" to redistribute).
export function distributeHorizontal(placements, selection) {
  const sel = selected(placements, selection)
  if (sel.length < 3) return placements
  // Sort by current visual-center-x so the endpoints stay where they are.
  const sorted = [...sel].sort((a, b) => getVisualCenterX(a) - getVisualCenterX(b))
  const firstC = getVisualCenterX(sorted[0])
  const lastC = getVisualCenterX(sorted[sorted.length - 1])
  const step = (lastC - firstC) / (sorted.length - 1)
  // Target visual-center-x for each placement, keyed by fieldId.
  const targets = new Map()
  sorted.forEach((p, i) => targets.set(p.fieldId, firstC + step * i))
  return placements.map((p) => {
    if (!targets.has(p.fieldId)) return p
    return { ...p, x: round2(xForVisualCenter(p, targets.get(p.fieldId))) }
  })
}

// Distribute selected placements so the gaps between their y centers are equal
// along the vertical axis. First and last (by y) stay fixed. Placements don't
// have a vertical anchor variant, so the visual-center-y is just `p.y`.
export function distributeVertical(placements, selection) {
  const sel = selected(placements, selection)
  if (sel.length < 3) return placements
  const sorted = [...sel].sort((a, b) => a.y - b.y)
  const firstY = sorted[0].y
  const lastY = sorted[sorted.length - 1].y
  const step = (lastY - firstY) / (sorted.length - 1)
  const targets = new Map()
  sorted.forEach((p, i) => targets.set(p.fieldId, firstY + step * i))
  return placements.map((p) => {
    if (!targets.has(p.fieldId)) return p
    return { ...p, y: round2(targets.get(p.fieldId)) }
  })
}
